/**
 * Procedural particle emitters for Theme Creator Particle System decorations.
 */
import {
  isThemeScreenVisible,
  type ThemeDecoration,
  type ThemeParticleConfig,
  type ThemeScreenTarget,
} from "./theme-types";
import { ANIM_FRAME_MS } from "./shop-creator-fx";

type Particle = {
  x: number;
  y: number;
  speed: number;
  driftPhase: number;
  rot: number;
  rotSpeed: number;
  scale: number;
  age: number;
  life: number;
  frame: number;
  frameAge: number;
};

type EmitterHost = "background" | "sidebar" | "viewport";

type Emitter = {
  key: string;
  decorId: string;
  host: EmitterHost;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  images: HTMLImageElement[];
  config: ThemeParticleConfig;
  opacity: number;
  screens: ThemeScreenTarget[];
  particles: Particle[];
  spawnAcc: number;
  lastW: number;
  lastH: number;
};

const emitters = new Map<string, Emitter>();
let raf = 0;
let lastTs = 0;
let frameTick = 0;

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("img"));
    img.src = src;
  });
}

function ensureCanvas(parent: HTMLElement, id: string, className: string): HTMLCanvasElement {
  let c = document.getElementById(id) as HTMLCanvasElement | null;
  if (!c) {
    c = document.createElement("canvas");
    c.id = id;
    c.className = className;
    c.setAttribute("aria-hidden", "true");
    parent.appendChild(c);
  } else if (c.parentElement !== parent) {
    parent.appendChild(c);
  }
  c.className = className;
  return c;
}

function emitterActive(em: Emitter, playing: boolean): boolean {
  if (em.host === "background") {
    return em.screens.includes("background") || em.screens.includes("all")
      ? !playing
      : em.screens.some((s) => s !== "sidebar" && isThemeScreenVisible(s, playing));
  }
  if (em.host === "sidebar") {
    if (playing) return false;
    if (em.screens.includes("sidebar") && em.screens.every((s) => s === "sidebar" || s === "all")) {
      return true;
    }
    return em.screens.some((s) => s === "sidebar" || isThemeScreenVisible(s, playing));
  }
  // viewport: any listed non-special screen
  return em.screens.some(
    (s) => s !== "sidebar" && s !== "background" && isThemeScreenVisible(s, playing),
  );
}

function spawnParticle(em: Emitter, w: number, h: number): Particle {
  const cfg = em.config;
  const angle = (cfg.windAngleDeg * Math.PI) / 180;
  // Spawn along the edge opposite to travel direction
  const ux = Math.sin(angle);
  const uy = Math.cos(angle);
  let x = Math.random() * w;
  let y = Math.random() * h;
  if (Math.abs(uy) >= Math.abs(ux)) {
    // mostly vertical
    if (uy >= 0) {
      y = -cfg.baseSizePx * 2;
      x = Math.random() * w;
    } else {
      y = h + cfg.baseSizePx * 2;
      x = Math.random() * w;
    }
  } else {
    if (ux >= 0) {
      x = -cfg.baseSizePx * 2;
      y = Math.random() * h;
    } else {
      x = w + cfg.baseSizePx * 2;
      y = Math.random() * h;
    }
  }
  const dist = Math.hypot(w, h) + cfg.baseSizePx * 4;
  const speed = rand(cfg.fallSpeedMin, cfg.fallSpeedMax);
  const life = Math.max(0.8, dist / Math.max(1, speed));
  return {
    x,
    y,
    speed,
    driftPhase: Math.random() * Math.PI * 2,
    rot: Math.random() * 360,
    rotSpeed: rand(cfg.rotationSpeedMin, cfg.rotationSpeedMax),
    scale: rand(cfg.scaleMin, cfg.scaleMax),
    age: 0,
    life,
    frame: em.images.length ? Math.floor(Math.random() * em.images.length) : 0,
    frameAge: 0,
  };
}

function stepEmitter(em: Emitter, dt: number, playing: boolean) {
  const parent = em.canvas.parentElement;
  if (!parent) return;
  const active = emitterActive(em, playing);
  em.canvas.classList.toggle("hidden", !active);
  if (!active) {
    em.particles.length = 0;
    em.spawnAcc = 0;
    return;
  }

  const rect = parent.getBoundingClientRect();
  const w = Math.max(1, Math.floor(rect.width));
  const h = Math.max(1, Math.floor(rect.height));
  if (em.lastW !== w || em.lastH !== h) {
    em.canvas.width = w;
    em.canvas.height = h;
    em.lastW = w;
    em.lastH = h;
  }

  const cfg = em.config;
  em.spawnAcc += cfg.spawnRate * dt;
  while (em.spawnAcc >= 1 && em.particles.length < cfg.maxParticles) {
    em.spawnAcc -= 1;
    em.particles.push(spawnParticle(em, w, h));
  }
  if (em.particles.length >= cfg.maxParticles) em.spawnAcc = 0;

  const angle = (cfg.windAngleDeg * Math.PI) / 180;
  const dirX = Math.sin(angle);
  const dirY = Math.cos(angle);
  // Perpendicular for drift
  const perpX = -dirY;
  const perpY = dirX;

  const next: Particle[] = [];
  for (const p of em.particles) {
    p.age += dt;
    p.frameAge += dt;
    if (em.images.length > 1 && p.frameAge * 1000 >= ANIM_FRAME_MS) {
      p.frameAge = 0;
      p.frame = (p.frame + 1) % em.images.length;
    }
    p.rot += p.rotSpeed * dt;
    const drift =
      cfg.driftAmplitude * Math.sin(p.driftPhase + p.age * cfg.driftFrequency * Math.PI * 2);
    p.x += (dirX * p.speed + perpX * drift * cfg.driftFrequency * 2) * dt;
    p.y += (dirY * p.speed + perpY * drift * cfg.driftFrequency * 2) * dt;

    const margin = cfg.baseSizePx * p.scale * 3;
    const out =
      p.x < -margin || p.x > w + margin || p.y < -margin || p.y > h + margin || p.age > p.life * 1.35;
    if (!out) next.push(p);
  }
  em.particles = next;

  const ctx = em.ctx;
  ctx.clearRect(0, 0, w, h);
  for (const p of em.particles) {
    const img = em.images[p.frame] || em.images[0];
    if (!img) continue;
    let alpha = em.opacity;
    if (cfg.opacityFade) {
      const t = p.age / p.life;
      const fadeIn = Math.min(1, t / 0.12);
      const fadeOut = Math.min(1, (1 - t) / 0.18);
      alpha *= Math.max(0, Math.min(fadeIn, fadeOut));
    }
    const size = cfg.baseSizePx * p.scale;
    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    ctx.translate(p.x, p.y);
    ctx.rotate((p.rot * Math.PI) / 180);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    ctx.restore();
  }
}

function tick(ts: number) {
  raf = requestAnimationFrame(tick);
  if (!lastTs) lastTs = ts;
  const dt = Math.min(0.05, (ts - lastTs) / 1000);
  lastTs = ts;
  frameTick++;
  const playing = document.body.classList.contains("game-active");
  for (const em of emitters.values()) {
    stepEmitter(em, dt, playing);
  }
  if (!emitters.size) {
    cancelAnimationFrame(raf);
    raf = 0;
    lastTs = 0;
  }
}

function startLoop() {
  if (raf) return;
  lastTs = 0;
  raf = requestAnimationFrame(tick);
}

function hostsForDecoration(dec: ThemeDecoration): EmitterHost[] {
  const hosts: EmitterHost[] = [];
  const screens = dec.screens;
  if (screens.includes("background") || screens.includes("all")) hosts.push("background");
  if (screens.includes("sidebar")) hosts.push("sidebar");
  const hasScreen = screens.some((s) => s !== "background" && s !== "sidebar" && s !== "all");
  if (hasScreen) hosts.push("viewport");
  if (!hosts.length) hosts.push("background");
  return hosts;
}

function parentForHost(host: EmitterHost): HTMLElement | null {
  if (host === "sidebar") return document.getElementById("sideNav");
  return document.body;
}

function classForHost(host: EmitterHost): string {
  if (host === "background") return "gameThemeParticleCanvas gameThemeParticleCanvas--bg";
  if (host === "sidebar") return "gameThemeParticleCanvas gameThemeParticleCanvas--sidebar";
  return "gameThemeParticleCanvas gameThemeParticleCanvas--viewport";
}

export async function syncThemeParticleEmitters(decorations: ThemeDecoration[]): Promise<void> {
  const wanted = new Set<string>();
  const particleDecs = decorations.filter((d) => d.kind === "particles" && d.particleConfig);

  for (const dec of particleDecs) {
    const hosts = hostsForDecoration(dec);
    const frameSrcs = [dec.frames?.[0] || dec.imageData].filter(
      (s): s is string => typeof s === "string" && s.length > 0,
    );
    let images: HTMLImageElement[] = [];
    try {
      images = await Promise.all(frameSrcs.map((s) => loadImg(s)));
    } catch {
      continue;
    }
    if (!images.length) continue;

    for (const host of hosts) {
      const parent = parentForHost(host);
      if (!parent) continue;
      const key = `${dec.id}__${host}`;
      wanted.add(key);
      let em = emitters.get(key);
      const canvasId = `gameThemeParticles_${dec.id}_${host}`;
      if (!em) {
        const canvas = ensureCanvas(parent, canvasId, classForHost(host));
        const ctx = canvas.getContext("2d")!;
        em = {
          key,
          decorId: dec.id,
          host,
          canvas,
          ctx,
          images,
          config: dec.particleConfig!,
          opacity: dec.opacity ?? 1,
          screens: [...dec.screens],
          particles: [],
          spawnAcc: 0,
          lastW: 0,
          lastH: 0,
        };
        emitters.set(key, em);
      } else {
        em.images = images;
        em.config = dec.particleConfig!;
        em.opacity = dec.opacity ?? 1;
        em.screens = [...dec.screens];
        em.canvas.className = classForHost(host);
      }
    }
  }

  for (const [key, em] of emitters) {
    if (wanted.has(key)) continue;
    em.canvas.remove();
    emitters.delete(key);
  }

  if (emitters.size) startLoop();
}

export function clearThemeParticleEmitters(): void {
  for (const em of emitters.values()) em.canvas.remove();
  emitters.clear();
  if (raf) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  lastTs = 0;
  void frameTick;
}
